<script setup lang="ts">
import { computed } from "vue";
import { statusColorName, type ChartStatus } from "./theme";

const props = withDefaults(
  defineProps<{
    percent: number;
    status?: ChartStatus;
    /** Leading label aligned to the left under the bar, usually the percentage achieved. */
    leadingLabel?: string;
    /** Trailing label aligned to the right under the bar, usually the target. */
    trailingLabel?: string;
    height?: number;
    /** Track in the soft shade of the status color instead of neutral grey. */
    tintedTrack?: boolean;
    /** "secondary" keeps the bar and its label black/grey, independent of the achievement status. */
    tone?: "status" | "secondary";
  }>(),
  {
    status: "success",
    height: 2,
    leadingLabel: undefined,
    trailingLabel: undefined,
    tintedTrack: false,
    tone: "status",
  }
);

const clampedPercent = computed(() => Math.min(100, Math.max(0, props.percent)));
const colorName = computed(() => statusColorName(props.status));
const isSecondaryTone = computed(() => props.tone === "secondary");

const ui = {
  root: "flex flex-col gap-1",
  track: "w-full overflow-hidden rounded-full",
  fill: "h-full rounded-full transition-all duration-500",
  labels: "flex items-center justify-between gap-2 text-xs",
  leadingLabel: "font-medium",
  leadingLabelSecondary: "text-secondary-700",
  trailingLabel: "text-secondary-700",
  fillSecondary: "bg-secondary-900",
};

const trackStyle = computed(() => ({ height: `${props.height}px` }));
const fillStyle = computed(() => ({ width: `${clampedPercent.value}%` }));
</script>

<template>
  <div :class="ui.root">
    <div
      :class="[ui.track, tintedTrack && !isSecondaryTone ? `bg-${colorName}-100` : 'bg-secondary-100']"
      :style="trackStyle"
      role="progressbar"
      :aria-valuenow="clampedPercent"
      aria-valuemin="0"
      aria-valuemax="100"
    >
      <div :class="[ui.fill, isSecondaryTone ? ui.fillSecondary : `bg-${colorName}-500`]" :style="fillStyle"></div>
    </div>

    <div v-if="leadingLabel || trailingLabel" :class="ui.labels">
      <span :class="[ui.leadingLabel, isSecondaryTone ? ui.leadingLabelSecondary : `text-${colorName}-600`]">
        {{ leadingLabel }}
      </span>
      <span :class="ui.trailingLabel">{{ trailingLabel }}</span>
    </div>
  </div>
</template>
